import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import StorefrontLayout from "../../components/storefront/StorefrontLayout";
import { api } from "../../lib/api";
import { fmtINR } from "../../lib/constants";
import { useAuth } from "../../lib/auth";
import { Trash2, ShoppingBag, ArrowRight, FileText } from "lucide-react";
import { toast } from "sonner";
import { assistGet, assistSubscribe } from "../../lib/assist";
import StaffExchangeCalc from "../../components/StaffExchangeCalc";

export default function Cart() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [prices, setPrices] = useState({});
  // Assist mode — staff building a cart for a walk-in customer
  const [assisting, setAssisting] = useState(assistGet());
  const [chosen, setChosen] = useState(new Set());   // pieces the customer is actually buying
  const [slipBusy, setSlipBusy] = useState(false);
  const [slipNote, setSlipNote] = useState("");
  const [credit, setCredit] = useState(0);       // customer's approved store credit
  const [applyCredit, setApplyCredit] = useState(0);

  useEffect(() => {
    if (assisting?.phone) {
      api.get(`/api/inventory/credits/mine?phone=${assisting.phone}`).then((r) => setCredit(r.data?.total || 0)).catch(() => setCredit(0));
    }
  }, [assisting]);
  const [exchangeValue, setExchangeValue] = useState(0);   // old gold taken in against this sale
  useEffect(() => assistSubscribe(() => setAssisting(assistGet())), []);

  const toggleChosen = (pid) => {
    const next = new Set(chosen);
    if (next.has(pid)) next.delete(pid); else next.add(pid);
    setChosen(next);
  };

  const generateSlip = async () => {
    if (chosen.size === 0) { toast.error("Tick the pieces the customer is buying"); return; }
    setSlipBusy(true);
    try {
      const { data } = await api.post("/api/crm/slip-to-bill", {
        customer_phone: assisting.phone,
        customer_name: assisting.name || "",
        product_ids: Array.from(chosen),
        // Which exact weight/size the customer picked for each design, so the
        // slip prices and reserves that specific piece.
        variant_labels: Object.fromEntries(
          (items || [])
            .filter((i) => chosen.has(i.product?.id) && i.variant_label_no)
            .map((i) => [i.product.id, i.variant_label_no])
        ),
        clear_other_cart_items: true,
        exchange_value: exchangeValue || 0,
        apply_credit: +applyCredit || 0,
        notes: exchangeValue > 0
          ? `${slipNote}${slipNote ? " | " : ""}Old gold exchange applied: Rs.${exchangeValue.toLocaleString("en-IN")}`
          : slipNote,
      });
      toast.success(`Slip ${data.slip_no} generated${data.credit_applied ? ` · ₹${data.credit_applied.toLocaleString("en-IN")} credit applied` : ""}`);
      // Open the printable PDF — but a PDF hiccup must NOT look like the slip failed.
      try {
        const resp = await api.get(`/api/crm/slip-to-bill/${data.id}/pdf`, { responseType: "blob" });
        const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 30000);
      } catch { toast.message("Slip saved. Open it from Slip-to-Bill if the PDF didn't pop up."); }
      setChosen(new Set());
      setSlipNote("");
      setExchangeValue(0);
      setApplyCredit(0);
      if (assisting?.phone) api.get(`/api/inventory/credits/mine?phone=${assisting.phone}`).then((r) => setCredit(r.data?.total || 0)).catch(() => {});
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate the slip");
    } finally { setSlipBusy(false); }
  };

  const applyExchange = (v) => {
    setExchangeValue(v);
    toast.success(`Old gold ₹${(v || 0).toLocaleString("en-IN")} applied to this sale`);
  };

  const load = async () => {
    if (!user) return;
    const { data } = await api.get("/api/cart");
    setItems(data.items || []);
    const pm = {};
    for (const it of data.items || []) {
      try {
        const { data: pd } = await api.get(`/api/products/${it.product.id}/price`);
        pm[it.product.id] = pd.price;
      } catch {}
    }
    setPrices(pm);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const update = async (pid, qty) => {
    if (qty <= 0) { await api.post("/api/cart/remove", { product_id: pid, qty: 1 }); }
    else { await api.post("/api/cart/add", { product_id: pid, qty, set_qty: true }); }
    load();
  };

  const remove = async (pid) => {
    await api.post("/api/cart/remove", { product_id: pid, qty: 1 });
    toast.success("Removed");
    load();
  };

  if (!user) return (
    <StorefrontLayout>
      <div className="px-6 lg:px-12 py-24 text-center">
        <ShoppingBag size={36} className="mx-auto text-maroon mb-4"/>
        <div className="font-display text-3xl text-maroon">Your cart awaits</div>
        <p className="text-ash mt-2">Please login to view your saved pieces.</p>
        <Link to="/login" className="btn-maroon mt-6 inline-flex" data-testid="cart-login-cta">Login</Link>
      </div>
    </StorefrontLayout>
  );

  // Unit price for a line — custom orders use their live weight×purity estimate.
  const lineUnit = (it) => (it.is_customised && it.custom_estimate ? it.custom_estimate : (prices[it.product.id] || 0));
  // Line totals = unit price × quantity (bug fix: qty was ignored)
  const subtotal = items.reduce((a, it) => a + lineUnit(it) * (it.qty || 1), 0);
  // Customised pieces are charged at 80% now, balance on delivery.
  const customisedSubtotal = items.reduce(
    (a, it) => a + (it.is_customised ? lineUnit(it) * (it.qty || 1) : 0), 0);
  const balanceLater = Math.round(customisedSubtotal * 0.2);

  return (
    <StorefrontLayout>
      <div className="px-6 lg:px-12 py-10">
        <h1 className="font-display text-4xl text-maroon">Your Cart</h1>
        <div className="royal-divider my-6"/>
        {items.length === 0 ? (
          <div className="text-center py-20 text-ash">
            Your cart is empty. <Link to="/catalog" className="text-maroon underline">Discover pieces →</Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              {items.map((it) => (
                <div key={it.product.id} className={`bg-white border p-4 flex gap-4 ${assisting && chosen.has(it.product.id) ? "border-maroon" : "border-line"}`} data-testid={`cart-item-${it.product.id}`}>
                  {assisting && (
                    <label className="flex items-start pt-1" title="Buying this piece">
                      <input
                        type="checkbox"
                        checked={chosen.has(it.product.id)}
                        onChange={() => toggleChosen(it.product.id)}
                        data-testid={`slip-pick-${it.product.id}`}
                      />
                    </label>
                  )}
                  <img src={it.product.images?.[0]} alt={it.product.name} className="w-24 h-28 object-cover bg-pearl"/>
                  <div className="flex-1">
                    <div className="eyebrow text-[10px]">{it.product.category}</div>
                    <Link to={`/product/${it.product.id}`} className="font-display text-xl text-ink hover:text-maroon">{it.product.name}</Link>
                    {/* Which weight the customer chose, when the design comes in several. */}
                    {it.variant_label_no && (
                      <div className="text-[11px] text-ash mt-0.5" data-testid={`cart-variant-${it.product.id}`}>
                        Selected: {it.product.weight_grams ? `${it.product.weight_grams} g` : "chosen piece"}
                      </div>
                    )}
                    {it.is_customised && (
                      <div className="mt-1" data-testid={`custom-badge-${it.product.id}`}>
                        <span className="text-[10px] uppercase tracking-widest bg-gold/25 text-maroon px-2 py-0.5">Customised · 80% advance</span>
                        {it.customisation_note && <div className="text-[11px] text-ash mt-1">{it.customisation_note}</div>}
                        {(it.customisation_images || []).length > 0 && (
                          <div className="flex gap-1 mt-1">
                            {it.customisation_images.slice(0, 4).map((u, i) => (
                              <img key={i} src={u} alt="" className="w-8 h-8 object-cover border border-line"/>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="text-xs text-ash mt-1">
                      {it.is_customised && it.custom_weight
                        ? `${it.custom_weight}g · ${it.custom_purity || ""} · custom`
                        : `${it.product.weight_grams}g · ${it.product.purity}`}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-maroon">{lineUnit(it) ? fmtINR(lineUnit(it) * (it.qty || 1)) : "…"}</div>
                    <div className="flex items-center gap-2 mt-2 justify-end">
                      <button onClick={() => update(it.product.id, (it.qty || 1) - 1)} className="border border-line w-6 h-6 text-sm" data-testid={`cart-dec-${it.product.id}`}>−</button>
                      <span className="text-xs w-4 text-center">{it.qty || 1}</span>
                      <button onClick={() => update(it.product.id, (it.qty || 1) + 1)} className="border border-line w-6 h-6 text-sm" data-testid={`cart-inc-${it.product.id}`}>+</button>
                    </div>
                    <button onClick={() => remove(it.product.id)} className="text-xs text-ash hover:text-maroon mt-3 flex items-center gap-1 ml-auto" data-testid={`cart-remove-${it.product.id}`}>
                      <Trash2 size={12}/> Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-white border border-line p-6 h-fit sticky top-32">
              <div className="eyebrow mb-4">Order Summary</div>
              <div className="flex justify-between text-sm mb-2"><span>Subtotal</span><span>{fmtINR(subtotal)}</span></div>
              <div className="flex justify-between text-sm mb-2 text-ash"><span>Delivery</span><span>{subtotal > 25000 ? "Free" : fmtINR(250)}</span></div>
              <div className="royal-divider my-4"/>
              <div className="flex justify-between font-display text-2xl text-maroon mb-2"><span>Total</span><span>{fmtINR(subtotal + (subtotal > 25000 ? 0 : 250))}</span></div>
              {exchangeValue > 0 && (
                <div className="border-t border-line pt-2 mb-2" data-testid="cart-exchange-summary">
                  <div className="flex justify-between text-sm text-green-700"><span>Less: old gold exchange</span><span>− {fmtINR(exchangeValue)}</span></div>
                  <div className="flex justify-between font-medium text-maroon mt-1"><span>Amount due</span><span>{fmtINR(Math.max(0, subtotal + (subtotal > 25000 ? 0 : 250) - exchangeValue))}</span></div>
                </div>
              )}
              {balanceLater > 0 && (
                <div className="text-[11px] text-ash mb-4 border-t border-line pt-2" data-testid="cart-advance-note">
                  <div className="flex justify-between text-maroon font-medium">
                    <span>Pay now</span>
                    <span>{fmtINR(subtotal + (subtotal > 25000 ? 0 : 250) - balanceLater)}</span>
                  </div>
                  <div className="flex justify-between mt-0.5">
                    <span>Balance on delivery (customised)</span><span>{fmtINR(balanceLater)}</span>
                  </div>
                </div>
              )}
              <div className="mb-4"/>
              <button onClick={() => nav("/checkout")} className="btn-maroon w-full justify-center" data-testid="checkout-btn">
                Checkout <ArrowRight size={14}/>
              </button>

              {/* Assist mode — hand the sale to the billing counter */}
              {assisting && (
                <div className="border-t border-line mt-6 pt-5" data-testid="slip-panel">
                  <div className="eyebrow mb-1">Slip to Bill</div>
                  <p className="text-[11px] text-ash mb-3">
                    Assisting <b className="text-maroon">{assisting.name || assisting.phone}</b>.
                    Tick the pieces being bought — the rest are cleared from the cart and a
                    printable slip is generated for the billing counter.
                  </p>
                  <textarea
                    rows={2}
                    placeholder="Note for billing (optional)"
                    className="w-full border border-line px-3 py-2 bg-pearl text-sm mb-2"
                    value={slipNote}
                    onChange={(e) => setSlipNote(e.target.value)}
                    data-testid="slip-note"
                  />
                  {exchangeValue > 0 && (
                    <div className="text-[11px] text-maroon mb-2" data-testid="slip-exchange-note">
                      Old gold exchange of <b>₹{exchangeValue.toLocaleString("en-IN")}</b> will be noted on the slip.
                    </div>
                  )}
                  {/* Apply store credit — only shown when the customer has > ₹1 */}
                  {credit > 1 && (
                    <div className="border border-gold bg-gold/10 rounded p-2.5 mb-2" data-testid="apply-credit-block">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-maroon font-medium">Store credit available: ₹{credit.toLocaleString("en-IN")}</span>
                        <button type="button" onClick={() => setApplyCredit(credit)} className="text-[11px] underline text-maroon">Use all</button>
                      </div>
                      <input
                        type="number" min="0" max={credit} placeholder="Apply credit ₹"
                        className="w-full border border-line px-3 py-2 bg-white text-sm"
                        value={applyCredit || ""}
                        onChange={(e) => setApplyCredit(Math.min(+e.target.value || 0, credit))}
                        data-testid="apply-credit-input"
                      />
                      {applyCredit > 0 && (
                        <div className="text-[10px] text-ash mt-1">
                          {applyCredit >= credit ? "Full credit" : "Partial credit"} — ₹{(+applyCredit).toLocaleString("en-IN")} will be deducted; remaining ₹{(credit - applyCredit).toLocaleString("en-IN")}.
                        </div>
                      )}
                    </div>
                  )}
                  <button
                    onClick={generateSlip}
                    disabled={slipBusy || chosen.size === 0}
                    className="btn-outline w-full justify-center inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="generate-slip"
                  >
                    <FileText size={14}/>
                    {slipBusy ? "Generating…" : `Generate Slip (${chosen.size})`}
                  </button>
                </div>
              )}

              {/* Old gold coming in against this sale */}
              {assisting && (
                <div className="border-t border-line mt-6 pt-5">
                  <StaffExchangeCalc compact onValue={(v) => applyExchange(v)} />
                </div>
              )}

              {/* Close the bill → billing desk (QR / counter / Razorpay) */}
              {assisting && (
                <CloseBillPanel
                  assisting={assisting}
                  amount={Math.max(0, subtotal + (subtotal > 25000 ? 0 : 250) - (exchangeValue || 0))}
                  items={items.map((it) => ({ product_id: it.product.id, name: it.product.name, qty: it.qty || 1 }))}
                  onClosed={() => { setExchangeValue(0); load(); }}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </StorefrontLayout>
  );
}

// Close the bill and hand it to the billing desk. QR shows the shop QR + UTR.
// "Close the bill" runs a 3-second safety countdown, then asks for the staff
// member's password before it actually submits.
function CloseBillPanel({ assisting, amount, items, onClosed }) {
  const [method, setMethod] = React.useState("qr");
  const [utr, setUtr] = React.useState("");
  const [qrUrl, setQrUrl] = React.useState("");
  const [credit, setCredit] = React.useState(0);
  const [countdown, setCountdown] = React.useState(0);
  const [askPw, setAskPw] = React.useState(false);
  const [password, setPassword] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => { api.get("/api/crm/payment-qr").then((r) => setQrUrl(r.data?.qr_url || "")).catch(() => {}); }, []);
  React.useEffect(() => { if (assisting?.phone) api.get(`/api/inventory/credits/mine?phone=${assisting.phone}`).then((r) => setCredit(r.data?.total || 0)).catch(() => {}); }, [assisting]);
  React.useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);
  React.useEffect(() => { if (countdown === 0 && askPw === "counting") setAskPw(true); }, [countdown, askPw]);

  const startClose = () => {
    if (method === "qr" && !utr.trim()) { toast.error("Enter the UTR after the customer pays via QR"); return; }
    setAskPw("counting"); setCountdown(3);
  };

  const submit = async () => {
    if (!password) { toast.error("Enter your password to confirm"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/api/crm/billing/close-bill", {
        customer_phone: assisting.phone, customer_name: assisting.name || "",
        amount, method, utr: utr.trim(), password, items,
      });
      toast.success(`Bill ${data.txn_no} sent to billing desk`);
      setAskPw(false); setPassword(""); setUtr(""); onClosed?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not close the bill"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border-t border-line mt-6 pt-5" data-testid="close-bill-panel">
      <div className="eyebrow mb-2">Close the bill</div>
      <div className="flex gap-2 mb-3">
        {[["qr", "QR code"], ["counter", "Pay at counter"], ["razorpay", "Razorpay"]].map(([m, label]) => (
          <button key={m} onClick={() => setMethod(m)} className={`px-2.5 py-1.5 text-xs border ${method === m ? "bg-maroon text-pearl border-maroon" : "border-line"}`} data-testid={`close-method-${m}`}>{label}</button>
        ))}
      </div>

      {method === "qr" && (
        <div className="mb-3">
          {qrUrl ? <img src={qrUrl} alt="Scan to pay" className="w-40 h-40 object-contain border border-line rounded mx-auto"/> : <div className="text-[11px] text-ash">No QR uploaded yet — ask the super admin to add it in Billing Desk.</div>}
          <input value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="UTR / reference after payment" className="w-full border border-line px-3 py-2 bg-pearl text-sm mt-2" data-testid="close-utr"/>
        </div>
      )}

      <div className="text-sm mb-3 flex justify-between"><span className="text-ash">Amount</span><b className="text-maroon">{fmtINR(amount)}</b></div>
      {credit > 1 && <div className="text-[11px] text-maroon mb-2" data-testid="closebill-credit-note">This customer has ₹{credit.toLocaleString("en-IN")} store credit — apply it on the slip before closing.</div>}

      {!askPw || askPw === "counting" ? (
        <button onClick={startClose} disabled={askPw === "counting"} className="btn-maroon w-full justify-center disabled:opacity-60" data-testid="close-bill-btn">
          {askPw === "counting" ? `Confirming in ${countdown}…` : "Close the bill"}
        </button>
      ) : (
        <div className="space-y-2" data-testid="close-bill-password">
          <p className="text-[11px] text-ash">Enter your password to confirm this sale is complete.</p>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" className="w-full border border-line px-3 py-2 bg-pearl text-sm" data-testid="close-password"/>
          <div className="flex gap-2">
            <button onClick={() => { setAskPw(false); setPassword(""); }} className="btn-outline text-sm flex-1">Cancel</button>
            <button onClick={submit} disabled={busy} className="btn-maroon flex-1 justify-center" data-testid="close-confirm">{busy ? "…" : "Confirm & send"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
